import { useState, useEffect, useRef, useCallback } from 'react';
import { newsAPI } from '../../lib/api';
import type { NewsEmailSupplier } from '../../lib/api/types/news';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Mail, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/** Props when emailing from a saved article */
interface SavedArticleProps {
  newsId: number;
  articleData?: never;
}

/** Props when emailing from unsaved form data (new article, no DB save) */
interface UnsavedArticleProps {
  newsId?: never;
  articleData: { title: string; summary: string; content: string; external_link?: string };
}

type EmailSuppliersModalProps = (SavedArticleProps | UnsavedArticleProps) & {
  newsTitle: string;
  open: boolean;
  onClose: () => void;
};

interface CommodityGroup { id: number; name: string; }
interface CommodityType  { id: number; name: string; commodity_group_id: number; }

const PAGE_SIZE = 20;

/**
 * Modal for selecting suppliers and sending them a news article via email.
 * Works for both saved articles (newsId) and unsaved form data (articleData).
 */
export default function EmailSuppliersModal({ newsId, articleData, newsTitle, open, onClose }: EmailSuppliersModalProps) {
  const isSaved = newsId !== undefined;

  const [suppliers, setSuppliers]       = useState<NewsEmailSupplier[]>([]);
  const [loading, setLoading]           = useState(false);
  const [sending, setSending]           = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Filters
  const [search, setSearch]                     = useState('');
  const [strategicFilter, setStrategicFilter]   = useState('all');
  const [commodityGroupId, setCommodityGroupId] = useState('all');
  const [commodityTypeId, setCommodityTypeId]   = useState('all');

  // Commodity dropdown data
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes]   = useState<CommodityType[]>([]);
  const [filteredTypes, setFilteredTypes]     = useState<CommodityType[]>([]);

  // Pagination & selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage]               = useState(1);
  const [totalCount, setTotalCount]   = useState(0);
  const [hasMore, setHasMore]         = useState(false);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef        = useRef<HTMLDivElement>(null);

  // Load commodity dropdowns once when modal opens
  useEffect(() => {
    if (!open) return;
    const fetch = isSaved
      ? newsAPI.getCommodityGroupsForEmail(newsId)
      : newsAPI.getCommodityGroupsForEmailPreview();

    fetch
      .then((res) => {
        setCommodityGroups(res.data.commodity_groups);
        setCommodityTypes(res.data.commodity_types);
        setFilteredTypes(res.data.commodity_types);
      })
      .catch(() => { /* non-critical */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Filter commodity types when group changes
  useEffect(() => {
    if (commodityGroupId === 'all') {
      setFilteredTypes(commodityTypes);
    } else {
      setFilteredTypes(commodityTypes.filter((t) => t.commodity_group_id === parseInt(commodityGroupId)));
    }
    setCommodityTypeId('all');
  }, [commodityGroupId, commodityTypes]);

  /** The filter set both the list and Select All send — one source of truth,
   *  so "select all" can never widen or narrow what the list is showing. */
  const buildParams = useCallback((pageNum: number, pageSize: number) => {
    const params: Record<string, string | number> = { page: pageNum, page_size: pageSize };
    if (search.trim())               params['search']              = search.trim();
    if (strategicFilter !== 'all')   params['is_preferred']        = strategicFilter;
    if (commodityGroupId !== 'all')  params['commodity_group_id']  = commodityGroupId;
    if (commodityTypeId !== 'all')   params['commodity_type_id']   = commodityTypeId;
    return params;
  }, [search, strategicFilter, commodityGroupId, commodityTypeId]);

  const loadSuppliers = useCallback(async (pageNum: number, reset: boolean) => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const params = buildParams(pageNum, PAGE_SIZE);

      const response = isSaved
        ? await newsAPI.getSuppliersForEmail(newsId, params as Parameters<typeof newsAPI.getSuppliersForEmail>[1])
        : await newsAPI.getSuppliersForEmailPreview(params as Parameters<typeof newsAPI.getSuppliersForEmailPreview>[0]);

      const data = response.data;
      setTotalCount(data.count);
      setHasMore(pageNum * PAGE_SIZE < data.count);
      setSuppliers((prev) => (reset ? data.results : [...prev, ...data.results]));
    } catch (err: unknown) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, strategicFilter, commodityGroupId, commodityTypeId]);

  // Reload on dropdown filter changes
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setSuppliers([]);
    loadSuppliers(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, strategicFilter, commodityGroupId, commodityTypeId]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      setSuppliers([]);
      loadSuppliers(1, true);
    }, 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSuppliers([]);
      setSearch('');
      setStrategicFilter('all');
      setCommodityGroupId('all');
      setCommodityTypeId('all');
      setCommodityGroups([]);
      setCommodityTypes([]);
      setFilteredTypes([]);
      setSelectedIds(new Set());
      setSelectingAll(false);
      setPage(1);
      setTotalCount(0);
      setHasMore(false);
      setError(null);
    }
  }, [open]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadSuppliers(nextPage, false);
    }
  };

  const toggleSupplier = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected  = suppliers.length > 0 && suppliers.every((s) => selectedIds.has(s.id));
  const someVisibleSelected = suppliers.some((s) => selectedIds.has(s.id));
  // The list is lazy-loaded a page at a time, so "every loaded row is ticked"
  // is NOT the same as "everyone is ticked". Select All means all `totalCount`
  // matches; the checkbox must only read as checked when that is actually true.
  const allMatchingSelected = totalCount > 0 && selectedIds.size >= totalCount;

  /** Walk every page of the current filter set and collect the ids. */
  const fetchAllMatchingIds = async (): Promise<number[]> => {
    const ids: number[] = [];
    const SELECT_ALL_PAGE_SIZE = 100;  // the backend caps page_size at 100
    for (let pageNum = 1; ; pageNum += 1) {
      const params = buildParams(pageNum, SELECT_ALL_PAGE_SIZE);
      const response = isSaved
        ? await newsAPI.getSuppliersForEmail(newsId, params as Parameters<typeof newsAPI.getSuppliersForEmail>[1])
        : await newsAPI.getSuppliersForEmailPreview(params as Parameters<typeof newsAPI.getSuppliersForEmailPreview>[0]);
      const data = response.data;
      ids.push(...data.results.map((s) => s.id));
      if (ids.length >= data.count || data.results.length === 0) break;
    }
    return ids;
  };

  const toggleSelectAll = async () => {
    if (allMatchingSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectingAll(true);
    setError(null);
    try {
      const ids = await fetchAllMatchingIds();
      setSelectedIds(new Set(ids));
    } catch (err: unknown) {
      console.error('Error selecting all suppliers:', err);
      setError('Could not select every supplier. Please try again.');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one supplier.');
      return;
    }
    setSending(true);
    try {
      const ids = Array.from(selectedIds);
      const response = isSaved
        ? await newsAPI.emailSuppliers(newsId, ids)
        : await newsAPI.emailSuppliersPreview({ ...articleData!, supplier_ids: ids });

      const data = response.data;
      if (data.status === 'success') {
        toast.success(data.message || 'Email sent successfully');
        onClose();
      } else {
        toast.error(data.message || 'Failed to send email');
      }
    } catch (err: unknown) {
      console.error('Error sending email:', err);
      toast.error('An error occurred while sending emails. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !sending) onClose(); }}>
      <DialogContent className="max-w-3xl w-[92vw] max-h-[85vh] flex flex-col gap-0 p-0">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Selected Suppliers
          </DialogTitle>
          <p className="text-sm text-muted-foreground truncate">
            Sending: <span className="font-medium">{newsTitle}</span>
          </p>
        </DialogHeader>

        {/* Filters */}
        <div className="px-5 pb-3 space-y-3 flex-shrink-0 border-b">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Strategic + Commodity Group */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Strategic</p>
              <Select value={strategicFilter} onValueChange={setStrategicFilter}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Strategic</SelectItem>
                  <SelectItem value="false">Non-Strategic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Commodity Group</p>
              <Select value={commodityGroupId} onValueChange={setCommodityGroupId}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="N/A" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">N/A</SelectItem>
                  {commodityGroups.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Commodity Type */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium">Commodity Type</p>
            <Select value={commodityTypeId} onValueChange={setCommodityTypeId}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="N/A" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">N/A</SelectItem>
                {filteredTypes.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Select All bar */}
        <div className="px-5 py-2 flex items-center justify-between border-b flex-shrink-0 bg-muted/30">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm">
            <Checkbox
              checked={allMatchingSelected ? true : ((someVisibleSelected || allVisibleSelected) ? 'indeterminate' : false)}
              onCheckedChange={toggleSelectAll}
              disabled={selectingAll || loading}
            />
            <span>Select All</span>
            {selectingAll && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </label>
          <span className="text-xs text-muted-foreground">
            {selectedIds.size} selected{totalCount > 0 ? ` / ${totalCount} total` : ''}
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pt-2 flex-shrink-0">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Supplier list */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto min-h-0 max-h-[55vh] px-3 py-2"
          style={{ maxHeight: '280px' }}
        >
          {suppliers.map((supplier) => (
            <label
              key={supplier.id}
              className="flex items-start gap-3 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer"
            >
              <Checkbox
                checked={selectedIds.has(supplier.id)}
                onCheckedChange={() => toggleSupplier(supplier.id)}
                className="mt-0.5 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{supplier.name}</span>
                  {supplier.is_preferred && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 flex-shrink-0">
                      Strategic
                    </span>
                  )}
                </div>
                {supplier.description && (
                  <p className="text-xs text-muted-foreground truncate">{supplier.description}</p>
                )}
              </div>
            </label>
          ))}

          {loading && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && suppliers.length === 0 && !error && (
            <p className="text-center text-sm text-muted-foreground py-8">No suppliers found.</p>
          )}

          {!loading && hasMore && (
            <p className="text-center text-xs text-muted-foreground py-2">Scroll down to load more</p>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 px-5 py-3 border-t gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || selectedIds.size === 0}>
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
            ) : (
              <><Mail className="h-4 w-4 mr-2" />Send Email{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
